import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { Logger } from '@utils/logger';
import { PortfolioComponentConfig } from './portfolio-component.config';
import {
  ChartData,
  ConfigurationId,
  FundId,
  PortfolioBaseType,
  PortfolioComponentId,
  PortfolioDataResponse,
  ProductDTO,
  SuppressionSet,
} from '@types';
import { SiteConfigService } from '@services/site-config.service';
import { GraphQLFundDataService } from '@products/services/graphql-fund-data.service';
import { Apollo } from 'apollo-angular';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import { ApolloQueryResult } from '@apollo/client/core';
import { map, publishLast, refCount, take } from 'rxjs/operators';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import {
  PortfolioAllocation,
  PortfolioTopHoldingTable,
  PortfolioSectorAllocationTable,
  Product,
  PortfolioAllocationTableData,
} from '@models';
import { ProductMapper } from '@products/utils/mappers/product.type.mapper';
import { TranslateService } from '@shared/translate/translate.service';
import { DebugService } from '@services/debug.service';
import { floatComparator } from '@utils/sort-utils';
import { Subject } from 'rxjs';
import {
  getFirstNonEmptyProp,
  hasValues,
} from '@products/utils/mappers/mapper.utils';

const logger = Logger.getLogger('PortfolioService');

const createBaseFimesKey = (
  baseLabelKey: string,
  allocationFlag?: string,
  hasDerivatives?: boolean,
  hasCurrency?: boolean
): string | null => {
  let base = '';

  // NB: specifically test for true/false, as shouldn't append if null or undefined
  if (hasDerivatives === true) {
    base += '-derivatives-yes';
  }
  if (hasDerivatives === false) {
    base += '-derivatives-no';
  }

  if (hasCurrency === true) {
    base += '-cash-yes';
  }
  if (hasCurrency === false) {
    base += '-cash-no';
  }

  // e.g. '-calc-basis-mv'
  if (allocationFlag) {
    base += '-calc-basis-' + allocationFlag.toLowerCase();
  }

  // if string is empty at this point, return null;
  if (!base) {
    return null;
  }

  // prepend with base label
  return `${baseLabelKey}-with${base}`;
};

interface PortfolioCache {
  [fundId: string]: {
    [componentId: string]: Observable<PortfolioDataResponse>;
  };
}

type PortfolioAllocationTable =
  | PortfolioTopHoldingTable
  | PortfolioSectorAllocationTable;

@Injectable({
  providedIn: 'root',
})
export class PortfolioService extends GraphQLFundDataService {
  private portfolioSelectionSubject$: Subject<void> = new Subject<void>();
  private cache: PortfolioCache = {};

  constructor(
    apollo: Apollo,
    siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private debugService: DebugService,
    private mapperFactory: MapperFactory
  ) {
    super(apollo, siteConfigService);
  }

  /**
   * Return required variables value to graphql query.
   * @param fundDataServiceParams params required for graphql query
   */
  private getVariables(fundId: FundId): Record<string, any> {
    return {
      countrycode: this.getCountry(),
      languagecode: this.getLanguage(),
      fundid: fundId,
    };
  }

  private createTooltip(
    labelKey: string,
    fimesLabelKey: string,
    isShowLabelKeys: boolean
  ): string {
    if (isShowLabelKeys) {
      let debugString = '';
      if (labelKey) {
        debugString += `[${labelKey}-tooltip]`;
      }
      if (labelKey && fimesLabelKey) {
        debugString += '<br />';
      }
      if (fimesLabelKey) {
        debugString += `[${fimesLabelKey}-tooltip]`;
      }
      return debugString;
    }

    const primaryTooltip: string = this.translateService.tooltip(labelKey);
    logger.debug('primaryTooltip:', labelKey, primaryTooltip);
    const secondaryTooltip: string = this.translateService.tooltip(
      fimesLabelKey
    );
    logger.debug('secondaryTooltip:', fimesLabelKey, secondaryTooltip);

    let tooltip = '';
    if (primaryTooltip) {
      tooltip += `<p>${primaryTooltip}</p>`;
    }
    if (secondaryTooltip) {
      tooltip += `<p>${secondaryTooltip}</p>`;
    }
    return tooltip;
  }

  private mapAllocationToChart(
    allocation: PortfolioAllocation,
    fundId: FundId,
    isShowLabelKeys: boolean
  ): ChartData {
    const baseFimesKey = createBaseFimesKey(
      allocation.baseLabelKey,
      allocation.allocationFlag,
      allocation.hasDerivatives,
      allocation.hasCurrency
    );
    const tooltipKey = `products.portfolio-${allocation.baseLabelKey}-heading`;
    const fimesTooltipKey = baseFimesKey
      ? `products.portfolio-${baseFimesKey}-heading`
      : null;
    return {
      fundId,
      chartTitle: this.translateService.instant(
        `products.portfolio-${allocation.baseLabelKey}-heading`
      ),
      caveatPlacement: `${
        allocation.caveatComponentId || allocation.componentId
      }:portfolio-${allocation.baseLabelKey}-caveat`,
      secondaryCaveatPlacement: baseFimesKey
        ? `${
          allocation.caveatComponentId || allocation.componentId
        }:portfolio-${baseFimesKey}-caveat`
        : null,
      tooltip: this.createTooltip(tooltipKey, fimesTooltipKey, isShowLabelKeys),
      asOfLabel: this.translateService.instant(`products.as-of-capitalize`),
      asOfDate: allocation.asOfDate,
      breakdownColumnTitle: allocation.breakdownColumnTitle,
      contributionDateRange: allocation.contributionDateRange,
      calculationBasis: allocation.calculationBasis,
      calculationType: allocation.calculationType,
      updateFrequency:
        allocation.updateFrequency &&
        this.translateService.instant(
          `products.updated-${allocation.updateFrequency}`
        ),
      proximalTopPlacement: `${allocation.baseLabelKey}-proximal-top`,
      proximalBottomPlacement: `${allocation.baseLabelKey}-proximal-bottom`,
      secondaryProximalTopPlacement: baseFimesKey
        ? `${baseFimesKey}-proximal-top`
        : null,
      secondaryProximalBottomPlacement: baseFimesKey
        ? `${baseFimesKey}-proximal-bottom`
        : null,
      highestPercent: allocation.data[0].breakdownStd,
      dataPoints: allocation.data,
      totalPercent: allocation.totalPercent,
      isPiChart: allocation.componentId === 'Asset Allocation' || false,
    };
  }

  public getPortfolioData$(
    config: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    logger.debug('cache getPortfolioData$() for ', config.componentId);
    // try to get data observable from cache
    let data$: Observable<PortfolioDataResponse> = this.cache[fundId]?.[
      config.componentId
      ];
    logger.debug('cache data found', data$);

    if (!data$) {
      // not in cache, so get fresh observable
      switch (config.baseType) {
        case PortfolioBaseType.PORTFOLIO_CHART:
          data$ = this.getPortfolioChartData$(config, fundId);
          break;
        case PortfolioBaseType.SECTOR_BREAKDOWN:
          data$ = this.getPortfolioTableData$(config, fundId);
          break;
        default:
          data$ = null;
      }
      logger.debug('cache get fresh data', data$);

      // set in cache, and share so doesn't make duplicate gql http calls
      if (!this.cache[fundId]) {
        this.cache[fundId] = {};
      }
      this.cache[fundId][config.componentId] = data$.pipe(
        take(1),
        publishLast(),
        refCount()
      );
    }

    logger.debug('cache contents', this.cache);
    return this.cache[fundId][config.componentId];
  }

  /**
   * Return portfolio holdings data.
   * @param config component config from blr
   * @param fundId target fund id
   */
  public getPortfolioTableData$(
    config: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    // 1. use combineLatest so result will be recalculated when debug mode toggled
    return combineLatest([
      // 2. get GraphQL fragment from component config and call register() on GraphQLFundDataService to get data
      super.register(config.gqlQuery, this.getVariables(fundId)),
      this.debugService.isPortfolioDebug$(),
    ]).pipe(
      map(
        ([productDetail, isDebugMode]: [
          ApolloQueryResult<{
            Portfolio: ProductDTO
          }>,
          boolean
        ]) => {
          logger.debug('holdings data from graphql', productDetail);
          // 3. Get data for this component and create MapperParams
          const productDto: ProductDTO = productDetail?.data?.Portfolio || null;
          const mapperParams: MapperParams = {
            config: this.siteConfigService,
            includeSoftClosed: true,
            translateService: this.translateService,
            fundId,
            isDebugMode,
          };

          // 4. map Product to get AssetClass
          const product: Product = this.mapperFactory
            .createMapper(ProductMapper, mapperParams)
            .toDomain(productDto);
          logger.debug('product', product, productDto);

          // 5. check config to see if component is suppressed
          const suppressionSet: SuppressionSet = this.siteConfigService.getPortfolioComponentSuppressionSet(
            config.componentId,
            fundId,
            product.assetClass
          );

          // 6. return early if component has been suppressed
          if (suppressionSet) {
            return {
              success: false,
              message: `Component "${config.componentId}" suppressed by Suppression Set "${suppressionSet.id}"`,
            };
          }

          let portfolioAllocationTableData: PortfolioAllocationTableData;

          // Implement allocations data to table as per product type received.

          // 7. map allocations dto to PortfolioHolding or PortfolioSectorAllocationTable object array.
          let allocations:
            | PortfolioTopHoldingTable[]
            | PortfolioSectorAllocationTable[] = [];
          allocations = this.mapperFactory
            .createMapper(config.mapper, mapperParams)
            .toDomain(productDto.portfolio);
          logger.debug('allocationsData', allocations, productDto);

          // 8. return early if component has no data
          if (!allocations || allocations.length === 0) {
            return {
              success: false,
              message: `Component "${config.componentId}" has no data`,
            };
          }
          // sort the holding data as per weight in desc order.
          if (
            allocations?.length > 0 &&
            config.componentId === PortfolioComponentId.TOP_TEN_HOLDINGS
          ) {
            allocations = (allocations as PortfolioTopHoldingTable[]).sort(
              (a, b) =>
                floatComparator(
                  b.breakdownPercentage,
                  a.breakdownPercentage,
                  null,
                  null,
                  true
                )
            );
          }

          const updateFrequency: string = getFirstNonEmptyProp(
            allocations,
            'updateFrequency'
          );
          const baseFimesKey = createBaseFimesKey(
            config.baseLabelKey,
            getFirstNonEmptyProp(allocations, 'allocationFlag'),
            getFirstNonEmptyProp(allocations, 'hasDerivatives')
          );
          portfolioAllocationTableData = {
            fundName: product.fundName,
            title: this.translateService.instant(
              `products.portfolio-${config.baseLabelKey}-heading`
            ),
            caveatPlacement: `${config.componentId}:${config.baseLabelKey}-caveat`,
            secondaryCaveatPlacement: baseFimesKey
              ? `${config.componentId}:${baseFimesKey}-caveat`
              : null,
            proximalTop: `${config.baseLabelKey}-proximal-top`,
            secondaryProximalTop: baseFimesKey
              ? `${baseFimesKey}-proximal-top`
              : null,
            proximalBottom: `${config.baseLabelKey}-proximal-bottom`,
            secondaryProximalBottom: baseFimesKey
              ? `${baseFimesKey}-proximal-bottom`
              : null,
            tooltip: this.translateService.tooltip(
              `products.portfolio-${config.baseLabelKey}-heading`
            ),
            asOfLabel: this.translateService.instant(
              'products.as-of-capitalize'
            ),
            asOfDate: getFirstNonEmptyProp(allocations, 'asOfDate'),
            calculationBasis: getFirstNonEmptyProp(
              allocations,
              'calculationBasis'
            ),
            calculationType: getFirstNonEmptyProp(
              allocations,
              'calculationType'
            ),
            updateFrequency:
              updateFrequency &&
              this.translateService.instant(
                `products.updated-${updateFrequency}`
              ),
            productType: product.productType as ConfigurationId,
            allocations,
            hideColumns: this.selectIdentifier(
              this.getColListWithNullData(allocations),
              fundId
            ),
          };
          logger.debug(
            'portfolioAllocationTableData',
            portfolioAllocationTableData
          );

          // 9. return success response with portfolioAllocationTableData
          return {
            success: true,
            payload: portfolioAllocationTableData,
          };
        }
      )
    );
  }

  /**
   * Filter columns names based upon null data in the same.
   * @param holdings portfolio holdings daily/full/top
   */
  getColListWithNullData(holdings: PortfolioAllocationTable[]): string[] {
    const hideCols: string[] = [];
    const allColumns: Array<keyof PortfolioAllocationTable> = holdings[0]
      ? (Object.keys(holdings[0]) as Array<keyof PortfolioAllocationTable>)
      : [];

    allColumns.forEach((column) => {
      // NGC-7048: Hide cols with all EMDASH values too.
      if (!hasValues(holdings, column)) {
        hideCols.push(column);
      }
    });

    return hideCols;
  }

  /**
   * show ISIN or CUSIP based on configuration
   */
  selectIdentifier(hideColumns: string[], fundId: FundId): string[] {
    if (
      this.siteConfigService.product.portfolio?.holdingsIdentifierOverride?.includes(
        fundId
      )
    ) {
      hideColumns.push('isinSecurityNumber');
    } else {
      hideColumns.push('cusipNumber');
    }
    return hideColumns;
  }

  public getPortfolioChartData$(
    portfolioComponentConfig: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    logger.debug(
      'getPortfolioChartData$() start for ',
      portfolioComponentConfig.componentId
    );
    // 1. get GraphQL query from component config and call register() on GraphQLFundDataService to get data
    return combineLatest([
      super.register(
        portfolioComponentConfig.gqlQuery,
        this.getVariables(fundId)
      ),
      this.debugService.isShowLabelKeys$(),
    ]).pipe(
      map(
        ([productDetail, isShowLabelKeys]: [
          ApolloQueryResult<{
            Portfolio: ProductDTO
          }>,
          boolean
        ]) => {
          logger.debug(
            'getPortfolioChartData$() map data for',
            portfolioComponentConfig.componentId
          );

          // 2. Get data for this component and create MapperParams
          const productDto: ProductDTO = productDetail?.data?.Portfolio || null;
          const mapperParams: MapperParams = {
            config: this.siteConfigService,
            translateService: this.translateService,
            includeSoftClosed: true,
          };

          // 3. map Product to get AssetClass
          const product: Product = this.mapperFactory
            .createMapper(ProductMapper, mapperParams)
            .toDomain(productDto);
          logger.debug('product', product, productDto);

          // 4. map allocationDTO to PortfolioAllocation object
          const allocations: PortfolioAllocation[] = this.mapperFactory
            .createMapper(portfolioComponentConfig.mapper, mapperParams)
            .toDomain(productDto.portfolio);
          logger.debug('allocations', allocations, productDto);

          // 5. return early if chart has no data
          if (!allocations || allocations.length === 0) {
            return {
              success: false,
              message: `Component "${portfolioComponentConfig.componentId}" has no data`,
            };
          }

          // 4. process allocations
          const payload: ChartData[] = [];
          let message = '';
          allocations.forEach((allocation: PortfolioAllocation): void => {
            // 5. check config to see if chart suppressed
            const suppressionSet: SuppressionSet = this.siteConfigService.getPortfolioComponentSuppressionSet(
              allocation.componentId,
              fundId,
              product.assetClass
            );
            if (suppressionSet) {
              // 6. Suppressed, so add to debug message
              message += `Component "${allocation.componentId}" suppressed by Suppression Set "${suppressionSet.id}"\n`;
            } else {
              // 7. Not suppressed, so map data and add to payload
              payload.push(
                this.mapAllocationToChart(allocation, fundId, isShowLabelKeys)
              );
            }
          });

          // 8. return data
          return {
            success: payload.length > 0,
            payload,
            message,
          };
        }
      )
    );
  }

  public get portfolioSelection$(): Observable<void> {
    return this.portfolioSelectionSubject$.asObservable();
  }

  public handlePortfolioTabSelection() {
    this.portfolioSelectionSubject$.next();
  }
}
